// vim: set ts=2 sts=2 sw=2 et:

import { PlatformLiveProfile } from "./platformLiveProfile";

export const PLATFORM_LIVE_BRIDGE_STATUS_PATH = "/wrpf/platform-live-bridge/status";
export const PLATFORM_LIVE_BRIDGE_START_PATH = "/wrpf/platform-live-bridge/start";
export const PLATFORM_LIVE_BRIDGE_STOP_PATH = "/wrpf/platform-live-bridge/stop";
export const PLATFORM_LIVE_BRIDGE_TOKEN_HEADER = "X-WRPF-Live-Bridge-Token";

export type PlatformLiveBridgeProviderId = "local" | "render" | "seenode";

export interface PlatformLiveBridgeConfig {
  readonly url: string;
  readonly token: string;
  /** Provider that owns this config; absent only for hand-built configs outside the provider model. */
  readonly id?: PlatformLiveBridgeProviderId;
}

export interface PlatformLiveBridgeProvider extends PlatformLiveBridgeConfig {
  readonly id: PlatformLiveBridgeProviderId;
  readonly label: string;
}

export interface PlatformLiveBridgeRoomStatus {
  readonly port?: number;
  readonly profile?: {
    readonly tournamentId?: string;
    readonly day?: number;
    readonly platformId?: number;
  };
}

export interface PlatformLiveBridgeStatus {
  readonly running: boolean;
  readonly port?: number;
  readonly suggestedLanHosts?: ReadonlyArray<string>;
  /** Salas vivas en el host (multi-tarima). Ausente en hosts viejos → usar `running`. */
  readonly rooms?: ReadonlyArray<PlatformLiveBridgeRoomStatus>;
}

export class PlatformLiveBridgeRequestError extends Error {
  readonly bridgeStatus: PlatformLiveBridgeStatus | null;

  constructor(message: string, bridgeStatus: PlatformLiveBridgeStatus | null) {
    super(message);
    this.name = "PlatformLiveBridgeRequestError";
    this.bridgeStatus = bridgeStatus;
  }
}

const trimTrailingSlash = (value: string): string => {
  return value.replace(/\/+$/, "");
};

const configuredCloudProvider = (
  id: PlatformLiveBridgeProviderId,
  label: string,
  url: string | undefined,
  token: string | undefined,
): PlatformLiveBridgeProvider | null => {
  if (url === undefined || url.trim().length === 0 || token === undefined || token.trim().length === 0) {
    return null;
  }
  return { id, label, url: trimTrailingSlash(url), token };
};

export interface PlatformLiveBridgeProviderOption {
  readonly id: PlatformLiveBridgeProviderId;
  readonly label: string;
  readonly configured: boolean;
}

const ALL_BRIDGE_PROVIDERS: ReadonlyArray<{
  readonly id: PlatformLiveBridgeProviderId;
  readonly label: string;
}> = [
  { id: "local", label: "Local" },
  { id: "render", label: "Render" },
  { id: "seenode", label: "Seenode" },
];

const configuredLocalProvider = (): PlatformLiveBridgeProvider | null => {
  const url = process.env.REACT_APP_WRPF_LIVE_BRIDGE_URL;
  const token = process.env.REACT_APP_WRPF_LIVE_BRIDGE_TOKEN;
  if (url === undefined || url.trim().length === 0 || token === undefined || token.trim().length === 0) {
    return null;
  }
  return { id: "local", label: "Local", url: trimTrailingSlash(url), token };
};

/** Configured providers: the local launcher bridge plus cloud hosts baked into a static build. */
export const getPlatformLiveBridgeProviders = (): ReadonlyArray<PlatformLiveBridgeProvider> => {
  const providers = [
    configuredLocalProvider(),
    configuredCloudProvider(
      "render",
      "Render",
      process.env.REACT_APP_WRPF_LIVE_BRIDGE_RENDER_URL,
      process.env.REACT_APP_WRPF_LIVE_BRIDGE_RENDER_TOKEN,
    ),
    configuredCloudProvider(
      "seenode",
      "Seenode",
      process.env.REACT_APP_WRPF_LIVE_BRIDGE_SEENODE_URL,
      process.env.REACT_APP_WRPF_LIVE_BRIDGE_SEENODE_TOKEN,
    ),
  ];
  return providers.filter((provider): provider is PlatformLiveBridgeProvider => provider !== null);
};

/** Every provider with its configuration state, so the launcher shows options without hiding any. */
export const getPlatformLiveBridgeProviderOptions = (): ReadonlyArray<PlatformLiveBridgeProviderOption> => {
  const configuredIds = new Set(getPlatformLiveBridgeProviders().map((provider) => provider.id));
  return ALL_BRIDGE_PROVIDERS.map(({ id, label }) => ({ id, label, configured: configuredIds.has(id) }));
};

export const getPlatformLiveBridgeProviderLabel = (providerId: PlatformLiveBridgeProviderId): string =>
  ALL_BRIDGE_PROVIDERS.find((provider) => provider.id === providerId)?.label || providerId;

export const getDefaultPlatformLiveBridgeProviderId = (): PlatformLiveBridgeProviderId | null => {
  const providers = getPlatformLiveBridgeProviders();
  const configuredDefault = process.env.REACT_APP_WRPF_LIVE_BRIDGE_DEFAULT_PROVIDER;
  if (configuredDefault === "local" || configuredDefault === "render" || configuredDefault === "seenode") {
    if (providers.some((provider) => provider.id === configuredDefault)) {
      return configuredDefault;
    }
  }
  return providers[0]?.id || null;
};

export const getPlatformLiveBridgeConfig = (
  providerId?: PlatformLiveBridgeProviderId | null,
): PlatformLiveBridgeConfig | null => {
  const providers = getPlatformLiveBridgeProviders();
  const requested = providerId || getDefaultPlatformLiveBridgeProviderId();
  const match = providers.find((provider) => provider.id === requested);
  return match === undefined ? null : { id: match.id, url: match.url, token: match.token };
};

/** True when the bridge is not loopback (cloud unified host). */
export const isRemotePlatformLiveBridge = (config: PlatformLiveBridgeConfig): boolean => {
  try {
    const hostname = new URL(config.url).hostname.toLowerCase();
    return hostname !== "127.0.0.1" && hostname !== "localhost";
  } catch (_error) {
    return false;
  }
};

const readBridgeError = async (response: Response): Promise<PlatformLiveBridgeRequestError> => {
  try {
    const body = (await response.json()) as { error?: unknown; running?: unknown; port?: unknown };
    const bridgeStatus =
      typeof body.running === "boolean"
        ? {
            running: body.running,
            port: typeof body.port === "number" ? body.port : undefined,
          }
        : null;
    if (typeof body.error === "string" && body.error.trim().length > 0) {
      return new PlatformLiveBridgeRequestError(body.error, bridgeStatus);
    }
  } catch (_error) {
    return new PlatformLiveBridgeRequestError(`Platform Live Bridge returned HTTP ${response.status}.`, null);
  }

  return new PlatformLiveBridgeRequestError(`Platform Live Bridge returned HTTP ${response.status}.`, null);
};

const requestBridge = async (
  config: PlatformLiveBridgeConfig,
  path: string,
  init: RequestInit,
): Promise<PlatformLiveBridgeStatus> => {
  const response = await fetch(`${config.url}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      [PLATFORM_LIVE_BRIDGE_TOKEN_HEADER]: config.token,
    },
  });

  if (!response.ok) {
    throw await readBridgeError(response);
  }

  return (await response.json()) as PlatformLiveBridgeStatus;
};

export const startPlatformLiveBridge = (
  profile: PlatformLiveProfile,
  config: PlatformLiveBridgeConfig,
): Promise<PlatformLiveBridgeStatus> => {
  return requestBridge(config, PLATFORM_LIVE_BRIDGE_START_PATH, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ profile }),
  });
};

export const stopPlatformLiveBridge = (
  config: PlatformLiveBridgeConfig,
  profile?: PlatformLiveProfile,
): Promise<PlatformLiveBridgeStatus> => {
  return requestBridge(
    config,
    PLATFORM_LIVE_BRIDGE_STOP_PATH,
    profile !== undefined
      ? {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ profile }),
        }
      : { method: "POST" },
  );
};

export const getPlatformLiveBridgeStatus = (config: PlatformLiveBridgeConfig): Promise<PlatformLiveBridgeStatus> => {
  return requestBridge(config, PLATFORM_LIVE_BRIDGE_STATUS_PATH, {
    method: "GET",
  });
};
